import PhotoSwipeLightbox from 'photoswipe/lightbox';
import Swiper from 'swiper';
import { Pagination, A11y } from 'swiper/modules';

const initializedGalleries = new WeakSet();
const initializedCards = new WeakSet();
const galleryLightboxes = new WeakMap();

const reduceMotion =
	typeof window.matchMedia === 'function' &&
	window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// The visible slide anchor — Swiper marks it with swiper-slide-active; single
// image cards (never initialized) fall back to their only slide.
function getActiveAnchor(card) {
	return (
		card.querySelector('.swiper-slide-active') ||
		card.querySelector('.card-slide')
	);
}

function initCard(card) {
	if (initializedCards.has(card)) return;

	const swiperEl = card.querySelector('.card-swiper');
	const slides = card.querySelectorAll('.card-slide');
	if (!swiperEl || slides.length <= 1) return;

	initializedCards.add(card);

	const bulletsEl = card.querySelector('.card-bullets');

	new Swiper(swiperEl, {
		modules: [Pagination, A11y],
		slidesPerView: 1,
		// A gutter keeps the next slide past the .card-swiper clip box so its
		// edge can't bleed a sub-pixel sliver onto the visible slide's right
		// edge (which reads as a thin dark line on cover-fit gallery images).
		spaceBetween: 16,
		speed: reduceMotion ? 0 : 400,
		loop: false,
		// Mouse drag enabled so cards stay swipeable on desktop (the old
		// hover-preview has been removed); touch / pen swipe regardless.
		grabCursor: true,
		threshold: 5,
		// Recalculate when a hidden (load-more) card is later revealed.
		observer: true,
		observeParents: true,
		pagination: bulletsEl
			? {
					el: bulletsEl,
					clickable: true,
					bulletClass: 'card-bullet',
					bulletActiveClass: 'is-active'
			  }
			: false,
		a11y: { enabled: true }
	});
}

// Lazy-init: a display:none (load-more) card never reports as intersecting, so
// its Swiper is only built once revealed and on screen — fixing zero-width init
// and avoiding building every off-screen slider up front.
const cardObserver =
	'IntersectionObserver' in window
		? new IntersectionObserver(
				(entries, obs) => {
					entries.forEach((entry) => {
						if (!entry.isIntersecting) return;
						obs.unobserve(entry.target);
						initCard(entry.target);
					});
				},
				{ rootMargin: '200px' }
		  )
		: null;

function observeCard(card) {
	if (initializedCards.has(card)) return;
	if (card.querySelectorAll('.card-slide').length <= 1) return;
	if (cardObserver) {
		cardObserver.observe(card);
	} else {
		initCard(card);
	}
}

function initLightboxes(scope = document) {
	const galleries = scope.querySelectorAll('[data-pswp-gallery]');

	galleries.forEach((gallery) => {
		if (initializedGalleries.has(gallery)) return;
		initializedGalleries.add(gallery);

		const lightbox = new PhotoSwipeLightbox({
			gallery,
			children: 'a.card-slide',
			pswpModule: () => import('photoswipe'),
			loop: true,
			bgOpacity: 0.95,
			showHideAnimationType: 'fade'
		});

		// The card image is no longer a zoom trigger — only the zoom button opens
		// the lightbox. Disable click-to-open for every slide, and stop the anchor
		// from navigating to its raw image URL.
		lightbox.addFilter('clickedIndex', (clickedIndex, event) => {
			if (event.target.closest('a.card-slide')) {
				event.preventDefault();
			}
			return -1;
		});

		lightbox.addFilter('domItemData', (itemData, element) => {
			itemData.caption = element.getAttribute('data-pswp-caption') || '';
			return itemData;
		});

		lightbox.on('uiRegister', () => {
			lightbox.pswp.ui.registerElement({
				name: 'caption',
				order: 9,
				isButton: false,
				appendTo: 'root',
				onInit: (el, pswp) => {
					el.classList.add('pswp__hide-on-close');
					pswp.on('change', () => {
						const caption =
							pswp.currSlide && pswp.currSlide.data
								? pswp.currSlide.data.caption
								: '';
						el.textContent = caption || '';
					});
				}
			});
		});

		lightbox.init();
		galleryLightboxes.set(gallery, lightbox);
	});
}

function initCards(scope = document) {
	scope.querySelectorAll('[data-product-card]').forEach(observeCard);
}

function init() {
	initLightboxes(document);
	initCards(document);

	document.addEventListener('click', (event) => {
		const zoomBtn = event.target.closest('.card-zoom');
		if (!zoomBtn) return;

		const card = zoomBtn.closest('[data-product-card]');
		if (!card) return;

		const galleryEl = card.closest('[data-pswp-gallery]');
		const lightbox = galleryEl && galleryLightboxes.get(galleryEl);
		if (!lightbox) return;

		// Zoom whatever image the visitor swiped to (or the featured image if
		// they never moved). getActiveAnchor reads Swiper's active slide.
		const activeAnchor = getActiveAnchor(card);
		if (!activeAnchor) return;

		// PhotoSwipe treats every a.card-slide in the gallery as one flat list,
		// so open at the active anchor's position within that same scope/order.
		const anchors = Array.from(galleryEl.querySelectorAll('a.card-slide'));
		const index = anchors.indexOf(activeAnchor);
		if (index >= 0) lightbox.loadAndOpen(index, { gallery: galleryEl });
	});

	document.addEventListener('skel:dom-updated', (event) => {
		const scope = event.target instanceof Element ? event.target : document;
		initLightboxes(scope);
		initCards(scope);
	});
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', init);
} else {
	init();
}
